import {
    Heading,
    Text,
    Container,
    Grid,
    Box,
    Flex,
    Spacer,
    HStack,
    Circle,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription
} from '@chakra-ui/react'
import { FiMapPin } from 'react-icons/fi'
import { useEffect, useState } from 'react'
import ReactSelect from 'react-select'
import { InfoCard } from '@/modules/teacher/components/InfoCard'
import { useAuth } from '@/shared/context/AuthContext'
import { DataTable } from '@/components/datatable/Datatable'
import { useFindAllCheckins } from '@/modules/teacher/hooks/useFindAllCheckins'
import { useBranches, BranchType } from '@/shared/api/hooks/useBranches'
import { useBranchDispatchContext } from '@/modules/teacher/context/BranchContext'
import { getFromLocalStorage } from '@/shared/utils/localStorageHelper'
import { homePageColumns } from '@/modules/teacher/utils/tableColumnHeader'
import { useAggregate } from '@/modules/teacher/hooks'
import { PROTECTED_ROUTES } from '@/shared/config/routes'
import { ProtectedPage } from '@/shared/components/ProtectedPage'

const today = new Date()

const Teacher = () => {
    const { user } = useAuth()
    const [branch, setBranch] = useState(getFromLocalStorage<BranchType>('currentBranch'))

    const dispatch = useBranchDispatchContext()
    const { branches, loading: branchLoading } = useBranches()
    const { data, loading } = useFindAllCheckins({
        startDate: today,
        branchId: branch ? branch.value : ''
    })

    const { data: aggregateData } = useAggregate({
        startDate: today,
        branchId: branch ? branch.value : ''
    })

    const handleBranchChange = (option: BranchType | null) => {
        if (option) {
            dispatch({
                type: 'set_branch',
                currentBranch: option
            })
            setBranch(option)
        }
    }

    useEffect(() => {}, [branch]) // to refetch "data" on branch change

    return (
        <ProtectedPage routeConfig={PROTECTED_ROUTES['/user/teacher']}>
            <Box
                minH={['container.sm', 'container.md']}
                borderRadius={4}
                bg={'white'}
                mx={'auto'}
                py={5}
                px={{ base: 2, md: 17 }}
            >
                <Flex alignItems="center" gap="2" flexWrap="wrap">
                    <Box p="2">
                        <Heading size="md" my={2}>
                            Welcome {user?.firstName} {user?.lastName}
                        </Heading>
                        <Text color={'gray.600'}>
                            Dear teacher, we log your check-in and check-out information
                            on this page
                        </Text>
                    </Box>
                    <Spacer />
                    <HStack p="2">
                        <Circle
                            size="35px"
                            bg="gray.50"
                            borderColor="brand.50"
                            borderWidth="1px"
                        >
                            <FiMapPin color="brand" size={'15px'} />
                        </Circle>
                        <ReactSelect
                            styles={{
                                control: (baseStyles) => ({
                                    ...baseStyles,
                                    width: '15rem'
                                })
                            }}
                            defaultValue={branch}
                            isLoading={branchLoading}
                            isSearchable
                            placeholder="Select branch"
                            options={branches}
                            onChange={handleBranchChange}
                        />
                    </HStack>
                </Flex>
                {!branch?.label ? (
                    <Alert status="warning" borderRadius={6} my={4} maxW={'fit-content'}>
                        <AlertIcon />
                        <Box>
                            <AlertTitle>Current branch is missing.</AlertTitle>
                            <AlertDescription>
                                Choose the branch you are having the service today. <br />
                                This is so that you see data related to your present
                                location
                            </AlertDescription>
                        </Box>
                    </Alert>
                ) : null}

                <Heading py={4} pl={2} as="h3" size="lg">
                    Summary
                </Heading>

                <Grid
                    p="2"
                    gap={4}
                    my={4}
                    templateColumns={{
                        sm: 'repeat(1, minmax(0, 1fr))',
                        md: 'repeat(2, minmax(0, 1fr))',
                        xl: 'repeat(4, minmax(0, 1fr))'
                    }}
                >
                    <InfoCard
                        bg="green.50"
                        textColor="brand"
                        stats={aggregateData.Toddlers.totalCheckedIn}
                        icon={<>✅</>}
                        label="Toddlers Check-in"
                    />
                    <InfoCard
                        bg="green.100"
                        textColor="brand.400"
                        stats={aggregateData.Toddlers.totalCheckedOut}
                        icon={<>⛔</>}
                        label="Toddlers Check-out"
                    />

                    <InfoCard
                        bg="blue.50"
                        textColor="brand.400"
                        stats={aggregateData.PreSchool.totalCheckedIn}
                        icon={<>✅</>}
                        label="PreSchool Check-in"
                    />
                    <InfoCard
                        bg="blue.100"
                        textColor="brand.400"
                        stats={aggregateData.PreSchool.totalCheckedOut}
                        icon={<>⛔</>}
                        label="PreSchool Check-out"
                    />

                    <InfoCard
                        bg="orange.50"
                        textColor="brand.400"
                        stats={aggregateData.SchoolAge.totalCheckedIn}
                        icon={<>✅</>}
                        label="SchoolAge Check-in"
                    />
                    <InfoCard
                        bg="orange.100"
                        textColor="brand.400"
                        stats={aggregateData.SchoolAge.totalCheckedOut}
                        icon={<>⛔</>}
                        label="SchoolAge Check-out"
                    />

                    <InfoCard
                        bg="pink.50"
                        textColor="brand.400"
                        stats={aggregateData.Samuels.totalCheckedIn}
                        icon={<>✅</>}
                        label="Samuels Check-in"
                    />
                    <InfoCard
                        bg="pink.100"
                        textColor="brand.400"
                        stats={aggregateData.Samuels.totalCheckedOut}
                        icon={<>⛔</>}
                        label="Samuels Check-out"
                    />

                    <InfoCard
                        bg="purple.50"
                        textColor="brand.400"
                        stats={aggregateData.Teenagers.totalCheckedIn}
                        icon={<>✅</>}
                        label="Teenagers Check-in"
                    />
                    <InfoCard
                        bg="purple.100"
                        textColor="brand.400"
                        stats={aggregateData.Teenagers.totalCheckedOut}
                        icon={<>⛔</>}
                        label="Teenagers Check-out"
                    />
                </Grid>

                <Container maxW={'full'} my={12}>
                    <Heading py="4" color={'brand.400'} fontSize="md">
                        Children List Today
                    </Heading>
                    {loading ? (
                        <Spinner
                            thickness="4px"
                            speed="0.65s"
                            emptyColor="brand.200"
                            color="brand.500"
                            size="xl"
                        />
                    ) : (
                        <DataTable columns={homePageColumns} data={data.checkInResults} />
                    )}
                </Container>
            </Box>
        </ProtectedPage>
    )
}

export default Teacher
